import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { sharedStyles as s } from "./identityVariants/shared.tsx";

interface TicketStatusItem {
  ID: number;
  Title: string;
  StatusID: number;
}

const DEFAULT_VALUE = "1,2,3,4";

/**
 * Ticket-status filter — segmented toggle pills. Emits a CSV of status IDs.
 * Defaults to "1,2,3,4" (the open states) if the filter mounts without a
 * pre-existing value.
 */
export function TicketStatusFilter({ info, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const statuses = useMemo(
    () => (cache?.TicketStatuses ?? []) as TicketStatusItem[],
    [cache],
  );

  const [selected, setSelected] = useState<Set<number>>(() => {
    if (value.length > 0 && value[0]?.oValue) {
      return new Set(String(value[0].oValue).split(",").map(Number).filter((n) => !isNaN(n)));
    }
    return new Set(DEFAULT_VALUE.split(",").map(Number));
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const mountedWithValues = useRef(value.length > 0);
  const didEmitDefault = useRef(false);

  useEffect(() => {
    if (didEmitDefault.current || statuses.length === 0) return;
    didEmitDefault.current = true;
    if (mountedWithValues.current) return;
    onChangeRef.current([{ sColumn: info.ColumnName, oValue: DEFAULT_VALUE, Type: ParamType.In }]);
  }, [statuses, info.ColumnName]);

  useEffect(() => {
    if (value.length === 0 && didEmitDefault.current) {
      setSelected(new Set(DEFAULT_VALUE.split(",").map(Number)));
      didEmitDefault.current = false;
      mountedWithValues.current = false;
    }
  }, [value.length]);

  const emit = useCallback(
    (next: Set<number>) => {
      if (next.size === 0) {
        onChangeRef.current([]);
      } else {
        onChangeRef.current([{ sColumn: info.ColumnName, oValue: Array.from(next).join(","), Type: ParamType.In }]);
      }
    },
    [info.ColumnName],
  );

  const handleToggle = useCallback(
    (id: number) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        emit(next);
        return next;
      });
    },
    [emit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSearch();
    },
    [onSearch],
  );

  const allIds = useMemo(() => statuses.map((x) => x.ID), [statuses]);
  const isAll = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const isNone = selected.size === 0;

  const setAll = (next: Set<number>) => {
    setSelected(next);
    emit(next);
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-1-5)", minWidth: 0 }}
      onKeyDown={handleKeyDown}
    >
      <div className={s.seg} style={{ flexWrap: "wrap" }}>
        {statuses.map((status) => (
          <button
            key={status.ID}
            type="button"
            className={selected.has(status.ID) ? s.segBtnActive : s.segBtn}
            onClick={() => handleToggle(status.ID)}
            aria-pressed={selected.has(status.ID)}
          >
            {status.Title}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: "var(--space-1)", alignItems: "center", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
        <button type="button" className={s.btnGhost} onClick={() => setAll(new Set(allIds))} disabled={isAll || allIds.length === 0}>
          All
        </button>
        <button type="button" className={s.btnGhost} onClick={() => setAll(new Set())} disabled={isNone}>
          Clear
        </button>
        <div style={{ flex: 1 }} />
        <span>
          {selected.size} / {statuses.length} selected
        </span>
      </div>
    </div>
  );
}
